import { useLayoutEffect, useRef, useState } from 'react';

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// Shrinks `el` (already showing `candidateText`) to the given font size, then repeatedly
// re-measures the REAL rendered box (scrollWidth vs clientWidth) and corrects — not an offline
// canvas estimate, which can never perfectly predict actual DOM text metrics (kerning, hinting,
// sub-pixel rounding) and was verified live to still leave a few px of overflow even with a
// generous safety margin, which is enough to trigger CSS text-overflow:ellipsis. Mutates
// `el.style.fontSize` and (for the compact-fallback path) `el.textContent` directly and
// synchronously within useLayoutEffect, before paint — safe because React reconciles its own
// fiber record against the new render output, not the live DOM, so a subsequent setState still
// produces the correct final commit regardless of this transient direct mutation.
const shrinkToFit = (el, candidateText, startPx, minPx) => {
  el.textContent = candidateText;
  let fontSize = startPx;
  for (let i = 0; i < 8; i++) {
    el.style.fontSize = `${fontSize}px`;
    const overflow = el.scrollWidth - el.clientWidth;
    if (overflow <= 0 || fontSize <= minPx) break;
    const ratio = el.clientWidth / el.scrollWidth;
    // A hair under the exact ratio so successive iterations converge from the safe side.
    fontSize = Math.max(minPx, fontSize * ratio * 0.98);
  }
  fontSize = clamp(fontSize, minPx, startPx);
  el.style.fontSize = `${fontSize}px`;
  return { fontSize, fits: el.scrollWidth <= el.clientWidth + 0.5 };
};

/**
 * Fits a single line of text to its container by shrinking font-size (never wrapping), falling
 * back to an abbreviated `compactText` only once even the minimum readable size wouldn't fit.
 * Reacts live to container resize (tiles are resizable via react-grid-layout) and to real
 * browser zoom (which fires a window resize event).
 */
export function useAutoFitFontSize({ text, maxPx, minPx, compactText, containerRef }) {
  const elRef = useRef(null);
  const [state, setState] = useState({ fontSizePx: maxPx, displayText: text, isCompact: false });

  useLayoutEffect(() => {
    const el = elRef.current;
    if (!el) return undefined;

    const fit = () => {
      const container = containerRef?.current || el.parentElement;
      if (!container || !container.clientWidth) return;

      const full = shrinkToFit(el, text, maxPx, minPx);
      if (full.fits || !compactText) {
        setState({ fontSizePx: full.fontSize, displayText: text, isCompact: false });
        return;
      }

      const compact = shrinkToFit(el, compactText, maxPx, minPx);
      setState({ fontSizePx: compact.fontSize, displayText: compactText, isCompact: true });
    };

    fit();

    let rafId = null;
    const scheduleFit = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        fit();
      });
    };

    const container = containerRef?.current || el.parentElement;
    const observer = container ? new ResizeObserver(scheduleFit) : null;
    if (container && observer) observer.observe(container);
    // Belt-and-suspenders alongside the ResizeObserver: browser zoom changes fire a window
    // resize event, so this guarantees a re-fit even in the unlikely case a zoom-driven layout
    // change doesn't produce a ResizeObserver entry for this particular container.
    window.addEventListener('resize', scheduleFit);

    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      observer?.disconnect();
      window.removeEventListener('resize', scheduleFit);
    };
  }, [text, compactText, maxPx, minPx, containerRef]);

  return { ref: elRef, ...state };
}
